import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { SearchField } from '../../../../components/SearchField';
import {
  EmptyState,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../../components/Table';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import { formatName } from '../../../../utils/formatName';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  Container,
  FiltersRow,
  HeaderRow,
  LockedState,
  PageSubtitle,
  PageTitle,
  RequestedBadge,
  Row,
  StudentAvatar,
  StudentInfo,
  StudentName,
} from './styles';

interface IPendingRequest {
  user_id: string;
  student_name: string;
  assessment_requested_at: string | null;
}

const PhysicalAssessmentRequests: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [requests, setRequests] = useState<IPendingRequest[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setLoading(true);

    api
      .get('measurements/requests')
      .then(response => {
        const sorted = [...response.data].sort(
          (a: IPendingRequest, b: IPendingRequest) =>
            new Date(a.assessment_requested_at || 0).getTime() -
            new Date(b.assessment_requested_at || 0).getTime(),
        );

        setRequests(sorted);
      })
      .catch(err => {
        if (err?.response?.status === 403) {
          setLocked(true);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredRequests = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return requests;

    return requests.filter(request =>
      request.student_name.toLowerCase().includes(search),
    );
  }, [requests, searchText]);

  return (
    <Painel loading={loading}>
      <Container>
        <HeaderRow>
          <div>
            <PageTitle>Avaliação física</PageTitle>
            <PageSubtitle>
              Alunos que solicitaram uma avaliação física (medidas) pelo
              aplicativo
            </PageSubtitle>
          </div>
        </HeaderRow>

        {locked && (
          <LockedState>
            <Icon name="straighten" size={40} color={colors.onSurfaceVariant} />
            <h2>Recurso não disponível no momento</h2>
            <PageSubtitle>
              Fale com o suporte pra saber mais sobre esse recurso.
            </PageSubtitle>
          </LockedState>
        )}

        {!locked && (
          <>
            <FiltersRow>
              <SearchField
                placeholder="Buscar por nome..."
                value={searchText}
                onChange={setSearchText}
              />
            </FiltersRow>

            <TableWrapper>
              <Table>
                <Thead>
                  <tr>
                    <Th>Aluno</Th>
                    <Th>Solicitado</Th>
                    <Th style={{ width: 40 }} />
                  </tr>
                </Thead>
                <Tbody>
                  {filteredRequests.map(request => (
                    <Row
                      key={request.user_id}
                      onClick={() =>
                        navigate(`/users/${request.user_id}/measurements`)
                      }
                    >
                      <Td>
                        <StudentInfo>
                          <StudentAvatar
                            src={noAvatar}
                            alt={request.student_name}
                          />
                          <StudentName>
                            {formatName(request.student_name)}
                          </StudentName>
                        </StudentInfo>
                      </Td>
                      <Td>
                        <RequestedBadge>
                          {request.assessment_requested_at
                            ? `há ${formatDistanceToNow(
                                parseISO(request.assessment_requested_at),
                                { locale: ptBR },
                              )}`
                            : '—'}
                        </RequestedBadge>
                      </Td>
                      <Td>
                        <Icon
                          name="chevron_right"
                          size={20}
                          color={colors.onSurfaceVariant}
                        />
                      </Td>
                    </Row>
                  ))}
                </Tbody>
              </Table>

              {!filteredRequests.length && (
                <EmptyState>
                  Nenhuma solicitação de avaliação física pendente.
                </EmptyState>
              )}
            </TableWrapper>
          </>
        )}
      </Container>
    </Painel>
  );
};

export { PhysicalAssessmentRequests };
